<template>
    <div>
        <base-wrapper
            :cardIcon="cardIcon"
            :cardText="data.Title"
            :cardValue="cardValue"
            :layoutConfig="layoutConfig"
            :showText="showText"
            :styles="getCardStyles"
            @show-modal="onShowModal"
            v-bind="$attrs"
            v-on="$listeners"
            :widget="data"
        />
        <update-dialog
            :layoutConfig="layoutConfig"
            :model="model"
            :visible.sync="showModal"
            @on-change="onChange"
            v-if="showModal">
            <template v-slot:content>
                <el-form @submit.native.prevent="onChange">
                    <el-form-item>
                        <div class="-mt-4 mb-2">
                            <label class="main-text-color">{{ $t('widget.title') }}</label>
                            <el-input v-model="model.Title"/>
                        </div>
                    </el-form-item>
                    <el-form-item>
                        <div class="py-2 flex">
                            <el-checkbox v-model="displayCardText">
                                {{ $t('status.show.text') }}
                            </el-checkbox>
                            <el-checkbox class="px-4" v-model="displayCardBorder">
                                {{ $t('status.display.border') }}
                            </el-checkbox>
                        </div>
                    </el-form-item>
                </el-form>
            </template>
            <template v-slot:additional-data>
                <time-frame
                    :model="model"
                    :timeFrameType="model.WidgetTime.type"
                    :widgetTimeOptions="widgetTimeOptions"
                    v-if="model && model.WidgetTime">
                    <template v-slot:frame-types>
                        <BaseRadioGroup
                            v-model="model.WidgetTime.type"
                            :radios="createWidgetTimeTypes"
                            class="radio-groups mb-5"
                        />
                    </template>
                </time-frame>
                <div class="flex items-center justify-between text-main-base" v-if="autoCompletes.length">
                    {{ $t('tooltip.refresh.entities.list') }}
                    <RefreshButton
                        :disabled="loadEntitiesList"
                        :loading="loadEntitiesList"
                        @click.native="refreshEntitiesList"/>
                </div>
                <el-collapse class="pt-4" v-model="activeCollapse" v-if="widgetConfig.filters.length">
                    <el-collapse-item :title="$t('settings.filters')" name="filters">
                        <div
                            v-for="(filter, index) in widgetConfig.filters"
                            :key="index"
                            class="py-4"
                        >
                            <AutoComplete
                                v-if="isAutoComplete(filter)"
                                :key="`auto-complete-${filter.ParameterID}`"
                                :model="getWidgetConfig(filter)"
                            />
                            <MultiSelect
                                v-if="isMultiSelect(filter)"
                                :key="`multiselect-${filter.ParameterID}`"
                                :model="getWidgetConfig(filter)"
                                @update-model="(changedModel) => updateWidgetConfig(changedModel, filter)"
                            />
                        </div>
                    </el-collapse-item>
                </el-collapse>
                <el-collapse class="pt-4" v-model="activeCollapse" v-if="widgetConfig.otherFilters.length">
                    <el-collapse-item :title="$t('settings.other.filters')" name="otherFilters">
                        <OtherFilters
                            v-for="(filter, index) in widgetConfig.otherFilters"
                            :key="index"
                            :model="filter"
                            class="mb-4"
                        />
                    </el-collapse-item>
                </el-collapse>
            </template>
            <template v-slot:footer>
                <div class="main-bg-light border-t-2 border-gray-300 py-4 px-10 flex items-center justify-between">
                    <cancel-button
                        @on-click="showModal = false"
                    />
                    <confirm-button
                        :label="$t('common.save')"
                        icon="IconSave"
                        @on-click="onChange"
                    />
                </div>
            </template>
        </update-dialog>
    </div>
</template>
<script>
    import get from 'lodash/get'
    import cloneDeep from 'lodash/cloneDeep'
    import RefreshButton from '@/components/RefreshButton'
    import { Checkbox, Collapse, CollapseItem, Tooltip } from 'element-ui'
    import { widgetTimeOptions, widgetTimeTypes } from '@/enum/widgetTimeOptions'
    import cardWidgetMixin from '@/mixins/cardWidgetMixin'
    import { defaultCardColors } from '@/enum/defaultWidgetSettings'
    import requestMixin from "@/mixins/requestMixin";
    import getDataIntervalMixin from '@/mixins/getDataIntervalMixin'
    import {getWidgetParameterConfig} from "@/helpers/widgetUtils";
    import widgetTemplateMixin from "@/mixins/widgetTemplateMixin";

    export default {
        mixins: [cardWidgetMixin, requestMixin, getDataIntervalMixin, widgetTemplateMixin],
        components: {
            TimeFrame: () => import('@/components/Widgets/WidgetUpdateForm/WidgetTime/TimeFrame'),
            OtherFilters: () => import('@/components/Widgets/Common/Filters/OtherFilters.vue'),
            AutoComplete: () => import('@/components/Widgets/Common/AutoComplete.vue'),
            MultiSelect: () => import('@/components/Widgets/Common/MultiSelect.vue'),
            UpdateDialog: () => import('./UpdateDialog'),
            RefreshButton,
            [Tooltip.name]: Tooltip,
            [Checkbox.name]: Checkbox,
            [Collapse.name]: Collapse,
            [CollapseItem.name]: CollapseItem,
            CancelButton: () => import("@/components/Common/Buttons/CancelButton"),
            ConfirmButton: () => import("@/components/Common/Buttons/ConfirmButton")
        },
        props: {
            data: {
                type: Object,
                default: () => ({}),
            },
            showText: {
                type: Boolean,
                default: true,
            },
            displayBorder: {
                type: Boolean,
                default: true,
            },
            cardIcon: {
                type: String,
                default: 'IconTotalOutgoingCalls',
            },
        },
        data() {
            return {
                cardValue: null,
                DATA_KEY: 'Total Outgoing Calls',
                showModal: false,
                layoutConfig: {},
                displayCardText: this.showText,
                displayCardBorder: this.displayBorder,
                model: {},
                loadEntitiesList: false,
                activeCollapse: 'filters',
                widgetTimeTypes,
                widgetTimeOptions,
            }
        },
        computed: {
            createWidgetTimeTypes () {
                return this.widgetTimeTypes
                    .map(el => {
                        return {
                            label: el.text, value: el.label
                        }
                    })
            }
        },
        methods: {
            get,
            async getWidgetData() {
                try {
                    let Data = await this.loadWidgetData(this.data)
                    this.cardValue = get(Data, `[0][${this.DATA_KEY}]`)
                } catch (e) {
                    console.log(e)
                }
            },
            onShowModal() {
                this.showModal = true
            },
            onChange() {
                try {
                    this.model.WidgetConfig = [ ...this.widgetConfig.filters, ...this.widgetConfig.otherFilters ]
                    this.model.WidgetConfig.forEach((config) => {
                        if (typeof config.WidgetParameterValueJson === 'object') {
                            config.WidgetParameterValueJson['AccountList'] = [this.$store.state.entities.selectedAccountID]
                        }
                        if (typeof config.WidgetParameterValue === 'object') {
                            config.WidgetParameterValue['AccountList'] = [this.$store.state.entities.selectedAccountID]
                            config.WidgetParameterValue = JSON.stringify(config.WidgetParameterValue)
                        }
                    })
                } catch (e) {
                }

                if (this.model.WidgetTime.type === 'relative') {
                    let widgetTime = widgetTimeOptions.find((el) => el.Date_interval === this.model.WidgetTime.Date_interval)
                    this.model.WidgetTime = {
                        ...this.model.WidgetTime,
                        ...widgetTime,
                    }
                }

                let data = {
                    showText: this.displayCardText,
                    displayBorder: this.displayCardBorder,
                }

                this.data.WidgetLayout = {
                    ...this.data.WidgetLayout,
                    ...data,
                    ...this.layoutConfig,
                    colors: this.model.colors,

                }

                this.data.WidgetTime = this.model.WidgetTime
                this.data.WidgetConfig = this.model.WidgetConfig
                this.data.Title = this.model.Title

                this.$emit('on-update', this.data)
                this.showModal = false
            },
            async refreshEntitiesList() {
                this.loadEntitiesList = true
                await this.$store.dispatch('entities/getEntitiesList')
                this.loadEntitiesList = false
            }
        },
        mounted() {
            this.getWidgetData()
        },
        watch: {
            data: {
                immediate: true,
                handler: function (widget) {
                    this.getDataWithRefreshInterval()
                    this.model = cloneDeep(widget)
                    this.model.colors = cloneDeep(widget.WidgetLayout.colors) || defaultCardColors
                },
            },
        },
    }
</script>
